// src/services/rapportiniAutoService.js

import { 
  collection, 
  query, 
  where, 
  getDocs,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  serverTimestamp,
  runTransaction  // 🆕 Per gestire concorrenza
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * ============================================================================
 * 📋 SERVIZIO RAPPORTINI AUTOMATICI v2.0
 * ============================================================================
 * 
 * Gestisce la creazione e l'aggiornamento automatico dei rapportini giornalieri
 * basandosi sul campo `clockInLocation` dei record timekeeping.
 * 
 * STRUTTURA TIMEKEEPING:
 * {
 *   userId: "user123",
 *   date: "2025-12-10",
 *   clockInTime: "08:00",
 *   clockInLocation: "cantiere-id-123",  // 🔑 CAMPO CHIAVE
 *   status: "in-progress",
 *   scanInfo: { clockIn: { ... } }
 * }
 * 
 * FONTI LOCATION:
 * - Kiosk: location automatica dal cantiere assegnato al dispositivo
 * - Admin/Responsabile: location selezionata manualmente tramite modal
 * - Qualsiasi altra fonte che specifichi clockInLocation
 * 
 * WORKFLOW:
 * 1. Dipendente timbra ingresso (da kiosk, admin, responsabile, ecc.)
 * 2. Sistema salva il cantiere nel campo clockInLocation
 * 3. Servizio verifica presenza di clockInLocation
 * 4. Verifica se esiste rapportino per quel cantiere/data
 * 5. Se non esiste → CREA rapportino vuoto
 * 6. Aggiunge il dipendente al rapportino con ore automatiche
 * 7. Alla timbratura di uscita → AGGIORNA le ore nel rapportino
 */

/**
 * 🔍 Recupera la location (cantiere) assegnata a un dispositivo kiosk
 * @param {string} deviceId - ID del dispositivo kiosk
 * @returns {string|null} - ID del cantiere o null se non trovato
 */
export const getKioskDeviceLocation = async (deviceId) => {
  try {
    console.log(`🔍 [RapportiniAuto] Recupero location per deviceId: ${deviceId}`);
    
    const deviceRef = doc(db, "scanDevices", deviceId);
    const deviceSnap = await getDoc(deviceRef);
    
    if (!deviceSnap.exists()) {
      console.log(`⚠️ [RapportiniAuto] Dispositivo ${deviceId} non trovato`);
      return null;
    }
    
    const deviceData = deviceSnap.data();
    
    // Verifica se il dispositivo ha una location assegnata
    if (!deviceData.locationId) {
      console.log(`⚠️ [RapportiniAuto] Dispositivo ${deviceId} non ha location assegnata`);
      return null;
    }
    
    console.log(`✅ [RapportiniAuto] Location trovata: ${deviceData.locationId}`);
    return deviceData.locationId;
    
  } catch (error) {
    console.error(`❌ [RapportiniAuto] Errore recupero location:`, error);
    return null;
  }
};

/**
 * 📋 Verifica se esiste un rapportino per cantiere e data
 * @param {string} cantiereId - ID del cantiere
 * @param {string} data - Data in formato YYYY-MM-DD
 * @returns {Object|null} - Rapportino esistente o null
 */
export const getRapportinoByCantiereAndDate = async (cantiereId, data) => {
  try {
    console.log(`🔍 [RapportiniAuto] Cerca rapportino: cantiere ${cantiereId}, data ${data}`);
    
    const rapportiniRef = collection(db, "rapportini");
    
    // ✅ PRIMA: Cerca con nomeRapportino (string "YYYY-MM-DD") - rapportini nuovi
    const qNome = query(
      rapportiniRef,
      where("cantiereId", "==", cantiereId),
      where("nomeRapportino", "==", data),  // ✅ String match
      where("stato", "==", "Attivo")
    );
    
    let querySnapshot = await getDocs(qNome);
    
    // ❌ Se non trovato con nomeRapportino, prova con data (timestamp) - rapportini vecchi
    if (querySnapshot.empty) {
      console.log(`⚠️ [RapportiniAuto] Non trovato con nomeRapportino, provo con data timestamp...`);
      
      const searchDate = new Date(data);
      const qData = query(
        rapportiniRef,
        where("cantiereId", "==", cantiereId),
        where("data", "==", searchDate),
        where("stato", "==", "Attivo")
      );
      
      querySnapshot = await getDocs(qData);
    }
    
    if (querySnapshot.empty) {
      console.log(`⚠️ [RapportiniAuto] Nessun rapportino trovato`);
      return null;
    }
    
    const doc = querySnapshot.docs[0];
    const rapportinoData = doc.data();
    
    console.log(`✅ [RapportiniAuto] Rapportino trovato:`, doc.id);
    
    return {
      id: doc.id,
      ...rapportinoData
    };
    
  } catch (error) {
    console.error(`❌ [RapportiniAuto] Errore ricerca rapportino:`, error);
    return null;
  }
};

/**
 * 📝 Crea un nuovo rapportino giornaliero vuoto
 * @param {string} cantiereId - ID del cantiere
 * @param {string} data - Data in formato YYYY-MM-DD
 * @param {Object} locationDetails - Dettagli del cantiere
 * @returns {string} - ID del rapportino creato
 */
export const createRapportinoGiornaliero = async (cantiereId, data, locationDetails = {}) => {
  try {
    console.log(`📝 [RapportiniAuto] Creazione rapportino: cantiere ${cantiereId}, data ${data}`);
    
    // Recupera i dettagli del cantiere se non forniti
    let cantiereData = locationDetails;
    if (!cantiereData.nomeCommessa) {
      const cantiereRef = doc(db, "cantieri", cantiereId);
      const cantiereSnap = await getDoc(cantiereRef);
      
      if (!cantiereSnap.exists()) {
        throw new Error(`Cantiere ${cantiereId} non trovato`);
      }
      
      cantiereData = cantiereSnap.data();
    }
    
    // Prepara i dati del rapportino
    const rapportinoData = {
      cantiereId: cantiereId,
      data: new Date(data),
      nomeRapportino: data,  // ✅ IMPORTANTE: Campo string "YYYY-MM-DD" per query VerificaPersonale
      
      // Informazioni cantiere
      nomeCantiere: cantiereData.nomeCommessa || 'Cantiere',
      luogo: cantiereData.luogo || '',
      committenteId: cantiereData.committenteId || '',
      
      // Sezioni vuote (verranno popolate durante la giornata)
      personalePresente: [],
      materialiForniti: [],
      mezziImpiegati: [],
      attrezzatureImpiegate: [],
      lavoriEseguiti: '',
      allegati: [],
      note: 'Rapportino creato automaticamente da sistema timbrature',
      
      // Metadati
      stato: 'Attivo',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      creatoAutomaticamente: true,
      modifiche: [{
        timestamp: new Date().toISOString(),
        tipo: 'creazione_automatica',
        descrizione: 'Rapportino creato automaticamente alla prima timbratura'
      }]
    };
    
    const rapportiniRef = collection(db, "rapportini");
    const docRef = await addDoc(rapportiniRef, rapportinoData);
    
    console.log(`✅ [RapportiniAuto] Rapportino creato con ID: ${docRef.id}`);
    
    return docRef.id;
    
  } catch (error) {
    console.error(`❌ [RapportiniAuto] Errore creazione rapportino:`, error);
    throw error;
  }
};

/**
 * 👤 Aggiunge o aggiorna il personale nel rapportino usando TRANSACTION
 * ⚠️ IMPORTANTE: Usa transaction per evitare race conditions quando più persone
 *    timbrano contemporaneamente sullo stesso cantiere
 * 
 * @param {string} rapportinoId - ID del rapportino
 * @param {Object} userData - Dati dell'utente da aggiungere
 * @param {Object} timekeepingData - Dati della timbratura
 * @returns {boolean} - Successo operazione
 */
export const aggiungiPersonaleRapportino = async (rapportinoId, userData, timekeepingData) => {
  try {
    console.log(`👤 [RapportiniAuto] Aggiunta personale al rapportino ${rapportinoId} con TRANSACTION`);
    
    const rapportinoRef = doc(db, "rapportini", rapportinoId);
    
    // 💰 RECUPERA COSTO ORARIO DELL'UTENTE
    let costoOrario = 0;
    if (userData.costoOrario !== undefined && userData.costoOrario !== null) {
      costoOrario = parseFloat(userData.costoOrario) || 0;
    } else if (userData.hourlyRate !== undefined && userData.hourlyRate !== null) {
      costoOrario = parseFloat(userData.hourlyRate) || 0;
    } else if (userData.tariffa !== undefined && userData.tariffa !== null) {
      costoOrario = parseFloat(userData.tariffa) || 0;
    }
    
    console.log(`💰 [RapportiniAuto] Costo orario ${userData.nome} ${userData.cognome}: €${costoOrario}/h`);
    
    // 🔒 USA TRANSACTION PER EVITARE RACE CONDITIONS
    await runTransaction(db, async (transaction) => {
      // 1. LEGGI il rapportino dentro la transaction
      const rapportinoSnap = await transaction.get(rapportinoRef);
      
      if (!rapportinoSnap.exists()) {
        throw new Error(`Rapportino ${rapportinoId} non trovato`);
      }
      
      const rapportinoData = rapportinoSnap.data();
      let personalePresente = rapportinoData.personalePresente || [];
      
      // 2. MODIFICA i dati
      const personaIndex = personalePresente.findIndex(p => p.personaId === userData.id);
      
      // 🔢 ASSICURATI CHE TUTTI I VALORI SIANO NUMERI (non stringhe!)
      const oreLavorate = parseFloat(timekeepingData.totalHours) || 0;
      const oreOrdinarie = parseFloat(timekeepingData.standardHours) || 0;
      const oreStraordinarie = parseFloat(timekeepingData.overtimeHours) || 0;
      
      // 💰 CALCOLA COSTI (tutti i valori devono essere numeri)
      const costoTotale = parseFloat((oreLavorate * costoOrario).toFixed(2));
      const costoOrdinario = parseFloat((oreOrdinarie * costoOrario).toFixed(2));
      const costoStraordinario = parseFloat((oreStraordinarie * costoOrario).toFixed(2));
      
      console.log(`📊 [RapportiniAuto] Calcolo costi: ${oreLavorate}h × €${costoOrario} = €${costoTotale}`);
      
      // 📝 COSTRUISCI NOTE IN BASE ALLO STATUS
      let notePersona = '';
      let turnoAutochiuso = false;
      let motivoAutochiusura = null;
      
      if (timekeepingData.status === 'in-progress') {
        notePersona = `Ingresso: ${timekeepingData.clockInTime} - Turno in corso`;
        
      } else if (timekeepingData.status === 'auto-closed') {
        // 🚨 TURNO AUTO-CHIUSO
        turnoAutochiuso = true;
        motivoAutochiusura = timekeepingData.autoClosedReason || 'Durata eccessiva (>18h)';
        
        notePersona = `${timekeepingData.clockInTime} - ${timekeepingData.clockOutTime} ` +
                      `⚠️ Auto-chiuso: ${motivoAutochiusura}. Assegnate ${oreLavorate}h standard.`;
        
        console.log(`⚠️ [RapportiniAuto] Turno autochiuso per ${userData.nome} ${userData.cognome}: ${motivoAutochiusura}`);
        
      } else {
        // Chiusura normale (completed)
        notePersona = `${timekeepingData.clockInTime} - ${timekeepingData.clockOutTime}`;
      }
      
      if (personaIndex >= 0) {
        // AGGIORNA persona esistente
        console.log(`🔄 [RapportiniAuto] Aggiornamento ore per ${userData.nome} ${userData.cognome}`);
        
        personalePresente[personaIndex] = {
          ...personalePresente[personaIndex],
          oreLavorate,
          oreOrdinarie,
          oreStraordinarie,
          costoOrario,
          costoTotale,
          totale: costoTotale,                       // campo letto da report e totali
          costoOrdinario,
          costoStraordinario,
          notePersona,
          turnoAutochiuso,                           // 🆕 Flag autochiusura
          motivoAutochiusura,                        // 🆕 Motivo se presente
          ultimoAggiornamento: new Date().toISOString()
        };
        
        console.log(`💰 [RapportiniAuto] Costi calcolati: Totale €${costoTotale.toFixed(2)} (Ord. €${costoOrdinario.toFixed(2)} + Straord. €${costoStraordinario.toFixed(2)})`);
        
      } else {
        // AGGIUNGI nuova persona
        console.log(`➕ [RapportiniAuto] Aggiunta ${userData.nome} ${userData.cognome} al rapportino`);
        
        const nuovaPersona = {
          personaId: userData.id,
          nome: userData.nome || '',
          cognome: userData.cognome || '',
          statoPresenza: 'presente',
          oreLavorate,
          oreOrdinarie,
          oreStraordinarie,
          costoOrario,
          costoTotale,
          totale: costoTotale,                       // campo letto da report e totali
          costoOrdinario,
          costoStraordinario,
          notePersona,
          turnoAutochiuso,                           // 🆕 Flag autochiusura
          motivoAutochiusura,                        // 🆕 Motivo se presente
          source: 'timbratura-automatica',
          timbratureInfo: {
            clockInTime: timekeepingData.clockInTime,
            clockOutTime: timekeepingData.clockOutTime || null,
            status: timekeepingData.status
          },
          aggiuntoAutomaticamente: true,
          dataAggiunta: new Date().toISOString()
        };
        
        personalePresente.push(nuovaPersona);
        
        console.log(`💰 [RapportiniAuto] Costi iniziali: €${costoOrario}/h (Totale €${costoTotale.toFixed(2)})`);
      }
      
      // 📊 CALCOLA TOTALI DEL RAPPORTINO (inclusi costi)
      // 🔢 Assicurati che tutti i valori siano numeri per i calcoli
      const totaleOreLavorate = personalePresente.reduce((sum, p) => {
        return sum + (parseFloat(p.oreLavorate) || 0);
      }, 0);
      
      const totaleOreOrdinarie = personalePresente.reduce((sum, p) => {
        return sum + (parseFloat(p.oreOrdinarie) || 0);
      }, 0);
      
      const totaleOreStraordinarie = personalePresente.reduce((sum, p) => {
        return sum + (parseFloat(p.oreStraordinarie) || 0);
      }, 0);
      
      // 💰 CALCOLA TOTALI COSTI (assicurati che siano numeri)
      const totaleCostoManodopera = parseFloat(
        personalePresente.reduce((sum, p) => {
          return sum + (parseFloat(p.costoTotale) || 0);
        }, 0).toFixed(2)
      );
      
      const totaleCostoOrdinario = parseFloat(
        personalePresente.reduce((sum, p) => {
          return sum + (parseFloat(p.costoOrdinario) || 0);
        }, 0).toFixed(2)
      );
      
      const totaleCostoStraordinario = parseFloat(
        personalePresente.reduce((sum, p) => {
          return sum + (parseFloat(p.costoStraordinario) || 0);
        }, 0).toFixed(2)
      );
      
      console.log(`📊 [RapportiniAuto] Totali rapportino: ${totaleOreLavorate}h (€${totaleCostoManodopera.toFixed(2)})`);
      console.log(`💰 [RapportiniAuto] Dettaglio costi - Ordinario: €${totaleCostoOrdinario.toFixed(2)}, Straordinario: €${totaleCostoStraordinario.toFixed(2)}`);
      
      // 3. SCRIVI dentro la transaction (atomico!)
      transaction.update(rapportinoRef, {
        personalePresente,
        totaleOreLavorate,
        totaleOreOrdinarie,
        totaleOreStraordinarie,
        numeroPersonalePresente: personalePresente.filter(p => p.statoPresenza === 'presente').length,
        totaleCostoManodopera,
        totaleCostoOrdinario,
        totaleCostoStraordinario,
        updatedAt: serverTimestamp(),
        ultimoAggiornamentoAutomatico: new Date().toISOString()
      });
    });
    
    console.log(`✅ [RapportiniAuto] Personale aggiornato con successo (TRANSACTION completata)`);
    return true;
    
  } catch (error) {
    console.error(`❌ [RapportiniAuto] Errore aggiunta personale:`, error);
    
    // 🔁 Se è un errore di concorrenza, Firestore riprova automaticamente
    if (error.code === 'aborted') {
      console.error(`⚠️ [RapportiniAuto] Transaction aborted - conflitto concorrenza rilevato`);
    }
    
    throw error;
  }
};

/**
 * 🔄 Gestisce la timbratura e aggiorna automaticamente il rapportino
 * Questa è la funzione principale da chiamare dopo clockIn/clockOut
 * 
 * @param {string} userId - ID dell'utente
 * @param {Object} timekeepingData - Dati completi del record timekeeping
 * @returns {Object} - Risultato operazione con dettagli rapportino
 */
export const handleTimbratureAutoRapportino = async (userId, timekeepingData) => {
  try {
    console.log(`🔄 [RapportiniAuto] Gestione timbratura per user ${userId}`);
    console.log(`📍 [RapportiniAuto] clockInLocation:`, timekeepingData.clockInLocation);
    
    // 1. Verifica che ci sia una location nel campo clockInLocation
    if (!timekeepingData.clockInLocation) {
      console.log(`⚠️ [RapportiniAuto] Nessuna clockInLocation - skip`);
      return {
        success: true,
        rapportinoCreated: false,
        rapportinoUpdated: false,
        message: 'Timbratura senza location - rapportino non gestito'
      };
    }
    
    const cantiereId = timekeepingData.clockInLocation;
    
    // 2. Recupera dati cantiere per verificare che esista e prendere il nome
    console.log(`🔍 [RapportiniAuto] Recupero dati cantiere: ${cantiereId}`);
    
    const cantiereRef = doc(db, "cantieri", cantiereId);
    const cantiereSnap = await getDoc(cantiereRef);
    
    if (!cantiereSnap.exists()) {
      console.log(`⚠️ [RapportiniAuto] Cantiere ${cantiereId} non trovato - skip`);
      return {
        success: false,
        rapportinoCreated: false,
        rapportinoUpdated: false,
        message: `Cantiere ${cantiereId} non trovato`
      };
    }
    
    const cantiereData = cantiereSnap.data();
    const cantiereName = cantiereData.nomeCommessa || 'Cantiere';
    
    console.log(`✅ [RapportiniAuto] Cantiere trovato: ${cantiereName}`);
    
    // 3. Recupera dati utente
    const userRef = doc(db, "users", userId);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      throw new Error(`Utente ${userId} non trovato`);
    }
    
    const userData = {
      id: userId,
      ...userSnap.data()
    };
    
    // 4. Estrai la data dalla timbratura
    const data = timekeepingData.date; // Formato YYYY-MM-DD
    
    // 5. Verifica se esiste già un rapportino
    let rapportino = await getRapportinoByCantiereAndDate(cantiereId, data);
    let rapportinoCreated = false;
    
    // 6. Se non esiste, crealo
    if (!rapportino) {
      console.log(`📝 [RapportiniAuto] Rapportino non esiste - creazione in corso...`);
      
      const rapportinoId = await createRapportinoGiornaliero(
        cantiereId,
        data,
        {
          nomeCommessa: cantiereName,
          luogo: cantiereData.luogo || '',
          committenteId: cantiereData.committenteId || ''
        }
      );
      
      // Recupera il rapportino appena creato
      rapportino = await getRapportinoByCantiereAndDate(cantiereId, data);
      rapportinoCreated = true;
    }
    
    // 7. Aggiungi/Aggiorna il personale nel rapportino
    await aggiungiPersonaleRapportino(rapportino.id, userData, timekeepingData);
    
    console.log(`✅ [RapportiniAuto] Rapportino gestito con successo`);
    
    return {
      success: true,
      rapportinoId: rapportino.id,
      rapportinoCreated,
      rapportinoUpdated: true,
      cantiereId: cantiereId,
      cantiereName: cantiereName,
      data,
      message: rapportinoCreated 
        ? `Rapportino creato e personale aggiunto per ${cantiereName}` 
        : `Personale aggiornato nel rapportino di ${cantiereName}`
    };
    
  } catch (error) {
    console.error(`❌ [RapportiniAuto] Errore gestione timbratura:`, error);
    return {
      success: false,
      error: error.message,
      message: `Errore: ${error.message}`
    };
  }
};

/**
 * 🔍 Recupera lo stato attuale dei rapportini per un cantiere
 * Utile per dashboard e statistiche
 */
export const getRapportiniStatus = async (cantiereId, dataInizio, dataFine) => {
  try {
    console.log(`📊 [RapportiniAuto] Recupero status rapportini cantiere ${cantiereId}`);
    
    const rapportiniRef = collection(db, "rapportini");
    
    const startDate = new Date(dataInizio);
    const endDate = new Date(dataFine);
    
    const q = query(
      rapportiniRef,
      where("cantiereId", "==", cantiereId),
      where("data", ">=", startDate),
      where("data", "<=", endDate),
      where("stato", "==", "Attivo")
    );
    
    const querySnapshot = await getDocs(q);
    
    const rapportini = [];
    querySnapshot.forEach(doc => {
      rapportini.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return {
      totale: rapportini.length,
      rapportiniAutomatici: rapportini.filter(r => r.creatoAutomaticamente).length,
      rapportiniManuali: rapportini.filter(r => !r.creatoAutomaticamente).length,
      rapportini
    };
    
  } catch (error) {
    console.error(`❌ [RapportiniAuto] Errore recupero status:`, error);
    throw error;
  }
};

export default {
  getKioskDeviceLocation,
  getRapportinoByCantiereAndDate,
  createRapportinoGiornaliero,
  aggiungiPersonaleRapportino,
  handleTimbratureAutoRapportino,
  getRapportiniStatus
};